/**
 * Where the feedback step's answers go: POST /v/feedback on the API that
 * served the scene (workers/backend/src/controls-feedback.ts). Kept apart from
 * the panel so the transport can change without touching the UI, and so it
 * still deletes with the folder.
 */

import type { UiLang } from '../ui/lang';
import type { Registry, SchemeId } from './control-schemes';

/** What the panel collects. */
export interface FeedbackAnswers {
    registry: Registry;
    lang: UiLang;
    /** Modes entered this visit, in registry order. */
    tried: SchemeId[];
    /** 1–5, only for tried modes that actually got stars. */
    ratings: Partial<Record<SchemeId, number>>;
    favourite: SchemeId | null;
    comment: string;
    /** Optional — blank means anonymous. */
    name: string;
}

/** One id per page load: a second send (they changed their mind) replaces the
 *  first instead of counting twice. */
const SUBMISSION_ID = (() => {
    try {
        return crypto.randomUUID();
    } catch {
        return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    }
})();

/** The endpoint lives on whatever origin served the scene — the same rule
 *  as the load beacon. A local/relative scene has no API behind it: null,
 *  and the panel offers no feedback button. */
export function feedbackEndpoint(sceneUrl: string): string | null {
    try {
        const u = new URL(sceneUrl, window.location.href);
        if (u.origin === window.location.origin) return null;
        return `${u.origin}/v/feedback`;
    } catch {
        return null;
    }
}

export async function sendFeedback(endpoint: string, answers: FeedbackAnswers, scene: string): Promise<boolean> {
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), 12000);
    try {
        // A plain string body goes out as text/plain, which keeps this a CORS
        // simple request — no preflight (see diagnostics.ts sendBeacon).
        const res = await fetch(endpoint, {
            method: 'POST',
            body: JSON.stringify({ id: SUBMISSION_ID, scene, ...answers }),
            signal: ctrl.signal
        });
        return res.ok;
    } catch {
        return false;
    } finally {
        clearTimeout(timer);
    }
}
